import {
  Box,
  HStack,
  Icon,
  SimpleGrid,
  Skeleton,
  Stat,
  StatArrow,
  StatHelpText,
  StatLabel,
  StatNumber,
  useColorModeValue,
} from '@elkaso-app/web-design';
import { useGetDedDetailsStatsApi } from 'apis/crm-ded-details';
import React from 'react';
import { FiCheckCircle, FiDatabase, FiPercent, FiPhone, FiPhoneOff, FiTrendingUp, FiXCircle } from 'react-icons/fi';

interface StatCardProps {
  title: string;
  value: string | number;
  helpText?: string;
  icon: React.ElementType;
  color: string;
  isLoading?: boolean;
  trend?: 'increase' | 'decrease';
  trendValue?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, helpText, icon, color, isLoading, trend, trendValue }) => {
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  return (
    <Box
      p={6}
      bg={bg}
      border='1px'
      borderColor={borderColor}
      borderRadius='lg'
      shadow='sm'
      _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
      transition='all 0.2s'>
      <Stat>
        <HStack justify='space-between' align='start' mb={2}>
          <StatLabel fontSize='sm' color='gray.600' fontWeight='medium'>
            {title}
          </StatLabel>
          <Icon as={icon} color={color} boxSize={5} />
        </HStack>

        {isLoading ? (
          <Skeleton height='32px' width='80px' />
        ) : (
          <StatNumber fontSize='2xl' fontWeight='bold' color={color}>
            {value}
          </StatNumber>
        )}

        {helpText && !isLoading && (
          <StatHelpText fontSize='xs' color='gray.500' mt={1}>
            {helpText}
          </StatHelpText>
        )}

        {trend && trendValue && !isLoading && (
          <StatHelpText fontSize='xs' mt={1}>
            <StatArrow type={trend} />
            {trendValue}
          </StatHelpText>
        )}
      </Stat>
    </Box>
  );
};

export const StatsCards: React.FC = () => {
  const { data: stats, isLoading } = useGetDedDetailsStatsApi();

  const calculateInactiveRate = () => {
    if (!stats || stats.total === 0) return '0.00';
    return (((stats.total - stats.active) / stats.total) * 100).toFixed(2);
  };

  const calculateNoContactRate = () => {
    if (!stats || stats.total === 0) return '0.00';
    return (((stats.total - stats.withContact) / stats.total) * 100).toFixed(2);
  };

  return (
    <SimpleGrid columns={{ base: 2, md: 4, lg: 7 }} spacing={4} mb={6}>
      <StatCard
        title='Total Businesses'
        value={stats?.total || 0}
        helpText='Total registered businesses'
        icon={FiDatabase}
        color='blue.500'
        isLoading={isLoading}
      />

      <StatCard
        title='Active Licenses'
        value={stats?.active || 0}
        helpText={`${stats?.activeRate || '0.00'}% of total`}
        icon={FiCheckCircle}
        color='green.500'
        isLoading={isLoading}
        trend='increase'
        trendValue={`${stats?.activeRate || '0.00'}%`}
      />

      <StatCard
        title='Inactive Licenses'
        value={stats?.inactive || 0}
        helpText={`${calculateInactiveRate()}% of total`}
        icon={FiXCircle}
        color='red.500'
        isLoading={isLoading}
        trend='decrease'
        trendValue={`${calculateInactiveRate()}%`}
      />

      <StatCard
        title='With Contact'
        value={stats?.withContact || 0}
        helpText={`${stats?.contactAvailabilityRate || '0.00'}% availability`}
        icon={FiPhone}
        color='purple.500'
        isLoading={isLoading}
        trend='increase'
        trendValue={`${stats?.contactAvailabilityRate || '0.00'}%`}
      />

      <StatCard
        title='No Contact'
        value={stats?.withoutContact || 0}
        helpText={`${calculateNoContactRate()}% without contact`}
        icon={FiPhoneOff}
        color='orange.500'
        isLoading={isLoading}
        trend='decrease'
        trendValue={`${calculateNoContactRate()}%`}
      />

      <StatCard
        title='Active Rate'
        value={`${stats?.activeRate || '0.00'}%`}
        helpText='License activation rate'
        icon={FiPercent}
        color='teal.500'
        isLoading={isLoading}
        trend='increase'
        trendValue='Good performance'
      />

      <StatCard
        title='Contact Rate'
        value={`${stats?.contactAvailabilityRate || '0.00'}%`}
        helpText='Contact availability rate'
        icon={FiTrendingUp}
        color='cyan.500'
        isLoading={isLoading}
        trend='increase'
        trendValue='Data quality metric'
      />
    </SimpleGrid>
  );
};
